import type { Property } from "@/hooks/useProperties";

export type PropertySearchFilters = {
  listingType: string;
  category: string;
  locationId: string;
  bedrooms: string;
  maxPrice: string;
};

type FilterableProperty = Pick<Property, "listing_type" | "category" | "location_id" | "bedrooms" | "price">;

const saleCategories = new Set(["offplan", "under-construction", "ready"]);
const rentCategories = new Set(["furnished", "unfurnished"]);

export const defaultPropertySearchFilters: PropertySearchFilters = {
  listingType: "",
  category: "",
  locationId: "",
  bedrooms: "",
  maxPrice: "",
};

export const bedroomOptions = ["1", "2", "3", "4", "5"];

export const maxPriceOptions = [
  { value: "50000000", label: "KES 50M" },
  { value: "100000000", label: "KES 100M" },
  { value: "200000000", label: "KES 200M" },
  { value: "500000000", label: "KES 500M" },
  { value: "1000000000", label: "KES 1B+" },
];

export const getCategoryOptions = (listingType?: string) => {
  if (listingType === "sale") {
    return [
      { value: "offplan", label: "Offplan" },
      { value: "under-construction", label: "Under Construction" },
      { value: "ready", label: "Ready" },
    ];
  }

  if (listingType === "rent") {
    return [
      { value: "furnished", label: "Furnished Rental" },
      { value: "unfurnished", label: "Unfurnished Rental" },
    ];
  }

  return [
    { value: "offplan", label: "Offplan" },
    { value: "under-construction", label: "Under Construction" },
    { value: "ready", label: "Ready" },
    { value: "furnished", label: "Furnished Rental" },
    { value: "unfurnished", label: "Unfurnished Rental" },
  ];
};

export const sanitizePropertySearchFilters = (
  filters: Partial<PropertySearchFilters>,
  fixedListingType?: string,
): PropertySearchFilters => {
  const listingType = fixedListingType ?? filters.listingType ?? "";
  const next = {
    ...defaultPropertySearchFilters,
    ...filters,
    listingType,
  };

  if (listingType === "sale" && rentCategories.has(next.category)) next.category = "";
  if (listingType === "rent" && saleCategories.has(next.category)) next.category = "";

  return next;
};

export const parsePriceToNumber = (price: string) => {
  const match = price
    .toUpperCase()
    .replace(/KES/g, "")
    .replace(/,/g, "")
    .match(/([0-9]+(?:\.[0-9]+)?)\s*([KMB])?/);

  if (!match) return null;

  const value = Number(match[1]);
  const multiplier = match[2] === "B" ? 1_000_000_000 : match[2] === "M" ? 1_000_000 : match[2] === "K" ? 1_000 : 1;

  return Number.isNaN(value) ? null : value * multiplier;
};

export const applyPropertySearchFilters = <T extends FilterableProperty>(properties: T[], rawFilters: Partial<PropertySearchFilters>) => {
  const filters = sanitizePropertySearchFilters(rawFilters);
  const minBedrooms = filters.bedrooms ? Number(filters.bedrooms) : null;
  const maxPrice = filters.maxPrice ? Number(filters.maxPrice) : null;

  return properties.filter((property) => {
    if (filters.listingType && property.listing_type !== filters.listingType) return false;
    if (filters.category && property.category !== filters.category) return false;
    if (filters.locationId && property.location_id !== filters.locationId) return false;
    if (minBedrooms && Number(property.bedrooms ?? 0) < minBedrooms) return false;

    if (maxPrice) {
      const propertyPrice = parsePriceToNumber(property.price ?? "");
      if (propertyPrice === null || propertyPrice > maxPrice) return false;
    }

    return true;
  });
};

export const filtersFromSearchParams = (searchParams: URLSearchParams, defaults?: Partial<PropertySearchFilters>) =>
  sanitizePropertySearchFilters({
    ...defaults,
    listingType: searchParams.get("listingType") ?? defaults?.listingType ?? "",
    category: searchParams.get("category") ?? defaults?.category ?? "",
    locationId: searchParams.get("locationId") ?? defaults?.locationId ?? "",
    bedrooms: searchParams.get("bedrooms") ?? defaults?.bedrooms ?? "",
    maxPrice: searchParams.get("maxPrice") ?? defaults?.maxPrice ?? "",
  });

export const filtersToSearchParams = (rawFilters: Partial<PropertySearchFilters>, fixedListingType?: string) => {
  const filters = sanitizePropertySearchFilters(rawFilters, fixedListingType);
  const params = new URLSearchParams();

  if (filters.listingType) params.set("listingType", filters.listingType);
  if (filters.category) params.set("category", filters.category);
  if (filters.locationId) params.set("locationId", filters.locationId);
  if (filters.bedrooms) params.set("bedrooms", filters.bedrooms);
  if (filters.maxPrice) params.set("maxPrice", filters.maxPrice);

  return params;
};